import "./BudgetGridSection.css";
import "../settings/settings-ui.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import { AltHintTarget } from "../../components/AltHintTarget/AltHintTarget.jsx";
import { ControlBar } from "../../components/ControlBar/ControlBar.jsx";
import { DataGrid } from "../../components/DataGrid/DataGrid.jsx";
import { FloatingActionToolbar } from "../../components/FloatingActionToolbar/FloatingActionToolbar.jsx";
import {
  buildFocusContextKey,
  createSlashToolbarBindings,
  useStableSelectionState,
  useSlashActionToolbar
} from "../../components/FloatingActionToolbar/useSlashActionToolbar.js";
import { Panel } from "../../components/Panel/Panel.jsx";
import { SearchField } from "../../components/SearchField/SearchField.jsx";
import { sortRows } from "../../components/DataGrid/sortRows.js";
import { SegmentedControl } from "../../components/SegmentedControl/SegmentedControl.jsx";
import {
  budgetGridHeaderGroups,
  budgetGridHeaderGroupsBasic,
  createBudgetGridColumnsBasic,
  createBudgetGridColumns
} from "./budgetGridColumns.js";
import { useShortcutBindings } from "../../lib/shortcuts/useShortcutBindings.js";
import { handlePageEscapeReset, hasPageEscapeResetAction } from "../../lib/shortcuts/pageEscapeReset.js";
import { useEscapeStack } from "../../lib/shortcuts/useEscapeStack.js";
import { createSectionNavigationBindings } from "../../lib/shortcuts/createSectionNavigationBindings.js";
import { BUDGET_VIEW_MODE_BASIC } from "../../lib/settings/profileSettings.js";
import { usePageStateMemory } from "../../lib/usePageStateMemory.js";
import { useRememberedSortState } from "../../lib/useRememberedSortState.js";
import { useSessionStorageState } from "../../lib/useSessionStorageState.js";
import {
  BUDGET_SEARCH_SESSION_KEY,
  setAnalysisChartCategory,
  setExpensesSearchQuery
} from "../../lib/pageSearchState.js";

import { createEmptyBudgetRow, isCompleteBudgetRow, isEmptyBudgetRow } from "./budgetDraftRow.js";

const BUDGET_SORT_SESSION_KEY = "wallet-watch:budget-sort";
const BUDGET_SELECTION_SESSION_KEY = "wallet-watch:budget-selection";

function getBudgetEmptyFooterProps({
  isVisible,
  hasPreviousYearBudgetRows,
  onImportPreviousYearBudget,
  onLoadSampleData,
  onStartBudgetEntry,
  t
}) {
  if (!isVisible) {
    return {
      footer: null,
      footerEnd: null
    };
  }

  return {
    footer: t("messages.sampleData.budgetEmptyBody"),
    footerEnd: (
      <>
        <button
          class="settings-action-button primary"
          type="button"
          onClick={() => onLoadSampleData?.()}
        >
          {t("messages.sampleData.loadSampleData")}
        </button>
        {hasPreviousYearBudgetRows ? (
          <button
            class="settings-action-button"
            type="button"
            onClick={() => onImportPreviousYearBudget?.()}
          >
            {t("messages.sampleData.importFromLastYear")}
          </button>
        ) : (
          <button
            class="settings-action-button"
            type="button"
            onClick={() => onStartBudgetEntry?.()}
          >
            {t("messages.sampleData.addBudgetEntry")}
          </button>
        )}
      </>
    )
  };
}

async function updateBudgetRowsForVisibleSelection({
  onRowsChange,
  pageRows,
  searchQuery,
  sortState,
  updateRows,
  visibleRows
}) {
  if (!searchQuery.trim() && !sortState) {
    await onRowsChange(updateRows);
    return;
  }

  const currentVisibleRows = visibleRows;
  const nextVisibleRows = updateRows(currentVisibleRows);
  const visibleIds = new Set(currentVisibleRows.map((row) => row.id).filter(Boolean));
  const nextVisibleRowsById = new Map(nextVisibleRows.map((row) => [row.id, row]));
  const nextRows = [];

  pageRows.forEach((row) => {
    if (!visibleIds.has(row.id)) {
      nextRows.push(row);
      return;
    }

    const nextVisibleRow = nextVisibleRowsById.get(row.id);
    if (nextVisibleRow) {
      nextRows.push(nextVisibleRow);
    }
  });

  nextVisibleRows.forEach((row) => {
    if (!visibleIds.has(row.id)) {
      nextRows.push(row);
    }
  });

  await onRowsChange(() => nextRows);
}

function deserializeBudgetSelection(storedValue) {
  try {
    const parsed = JSON.parse(storedValue);
    return {
      rowId: typeof parsed?.rowId === "string" ? parsed.rowId : null,
      columnKey: typeof parsed?.columnKey === "string" ? parsed.columnKey : "category"
    };
  } catch {
    return { rowId: null, columnKey: "category" };
  }
}

function serializeBudgetSelection(value) {
  return JSON.stringify({
    rowId: value?.rowId ?? null,
    columnKey: value?.columnKey ?? "category"
  });
}

function getActiveBudgetColumns(detailMode, basicColumns, advancedColumns) {
  return detailMode === BUDGET_VIEW_MODE_BASIC ? basicColumns : advancedColumns;
}

function getActiveBudgetHeaderGroups(detailMode, t) {
  return detailMode === BUDGET_VIEW_MODE_BASIC ? budgetGridHeaderGroupsBasic(t) : budgetGridHeaderGroups(t);
}

function useRememberedBudgetSelection(gridApiRef, gridReady, selectedCellState) {
  const didRestoreSelectionRef = useRef(false);
  const [rememberedSelection, setRememberedSelection] = usePageStateMemory(BUDGET_SELECTION_SESSION_KEY, {
    rowId: null,
    columnKey: "category"
  }, {
    deserialize: deserializeBudgetSelection,
    serialize: serializeBudgetSelection
  });

  useEffect(() => {
    if (!didRestoreSelectionRef.current) {
      return;
    }

    if (!selectedCellState?.row?.id || !selectedCellState?.column?.key) {
      return;
    }

    setRememberedSelection({
      rowId: selectedCellState.row.id,
      columnKey: selectedCellState.column.key
    });
  }, [selectedCellState, setRememberedSelection]);

  useEffect(() => {
    if (!gridReady || !gridApiRef.current || didRestoreSelectionRef.current) {
      return;
    }

    didRestoreSelectionRef.current = true;
    gridApiRef.current.restoreSelection({
      rowId: rememberedSelection?.rowId ?? null,
      columnKey: rememberedSelection?.columnKey ?? "category",
      focus: true
    });
  }, [gridApiRef, gridReady, rememberedSelection?.columnKey, rememberedSelection?.rowId]);
}

export function BudgetGridSection({
  page,
  onNavigateToPage,
  onRowsChange,
  budgetDraftEntryRequest,
  onLoadSampleData,
  onImportPreviousYearBudget,
  onStartBudgetEntry,
  hasPreviousYearBudgetRows = false,
  detailMode,
  onDetailModeChange,
  showAltHints = false,
  t = (key) => key
}) {
  const pageRef = useRef(null);
  const searchInputRef = useRef(null);
  const detailModeControlRef = useRef(null);
  const gridApiRef = useRef(null);
  const [gridReady, setGridReady] = useState(false);
  const [searchQuery, setSearchQuery] = useSessionStorageState(BUDGET_SEARCH_SESSION_KEY, "");
  const [sortState, setSortState] = useRememberedSortState(BUDGET_SORT_SESSION_KEY);
  const { handleSelectionChange, selectedCellState } = useStableSelectionState();
  const [focusContext, setFocusContext] = useState(null);
  const lastHandledDraftRequestRef = useRef(0);
  useRememberedBudgetSelection(gridApiRef, gridReady, selectedCellState);

  const focusMainArea = useCallback(() => {
    const gridElement = pageRef.current?.querySelector('[data-role="data-grid"]');
    gridElement?.focus({ preventScroll: true });
  }, []);
  const detailModeOptions = useMemo(() => ([
    { value: "basic", label: t("basic") },
    { value: "advanced", label: t("advanced") }
  ]), [t]);

  const rowEditing = useMemo(() => ({
    appendEmptyRow: true,
    allowDeleteRow: true,
    createEmptyRow: createEmptyBudgetRow,
    createDuplicateRow: (row) => ({
      ...row,
      id: crypto.randomUUID(),
      archived: false,
      archivedAt: null
    }),
    isRowEmpty: isEmptyBudgetRow,
    isRowComplete: isCompleteBudgetRow
  }), []);
  const selectedCategoryValue = selectedCellState?.column?.key === "category"
    ? String(selectedCellState?.row?.category ?? "").trim()
    : "";
  const focusContextKey = buildFocusContextKey(focusContext);
  const canOpenContextToolbar = Boolean(
    selectedCellState
    && focusContext?.focused
    && !selectedCellState.editing
    && selectedCellState.column?.key === "category"
    && selectedCategoryValue
  );
  const toolbarActions = useMemo(() => ([
    {
      key: "show-in-expenses",
      label: t("expenses"),
      onSelect: () => {
        setExpensesSearchQuery(selectedCategoryValue);
        onNavigateToPage?.("expenses");
      }
    },
    {
      key: "show-in-chart",
      label: t("chart"),
      onSelect: () => {
        setAnalysisChartCategory(selectedCategoryValue);
        onNavigateToPage?.("analysis", "charts");
      }
    }
  ]), [onNavigateToPage, selectedCategoryValue, t]);
  const {
    setSelectedIndex,
    setToolbarOpen,
    toolbarActions: renderedToolbarActions,
    toolbarOpen
  } = useSlashActionToolbar({
    canOpen: canOpenContextToolbar,
    actions: toolbarActions,
    focusContextKey,
    gridFocused: Boolean(focusContext?.focused)
  });
  const hasEscapeAction = hasPageEscapeResetAction({
    searchQuery,
    sortState
  });
  useEscapeStack(() => handlePageEscapeReset({
    searchQuery,
    setSearchQuery,
    sortState,
    setSortState,
    onResetApplied: focusMainArea
  }), hasEscapeAction, -100);
  const pageBindings = useMemo(() => createSlashToolbarBindings({
    canOpen: canOpenContextToolbar,
    focusMainArea,
    focusPrimaryControl: () => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select?.();
    },
    focusSecondaryControl: () => {
      const activeButton = detailModeControlRef.current?.querySelector(".segmented-control-option.active");
      const fallbackButton = detailModeControlRef.current?.querySelector(".segmented-control-option");
      (activeButton ?? fallbackButton)?.focus();
    },
    openToolbar: () => {
      setSelectedIndex(0);
      setToolbarOpen(true);
    },
    startDraftRowEdit: () => {
      gridApiRef.current?.startDraftRowEdit();
    }
  }), [canOpenContextToolbar, focusMainArea, setSelectedIndex, setToolbarOpen]);
  const detailModeBindings = useMemo(() => createSectionNavigationBindings({
    options: detailModeOptions,
    value: detailMode,
    onChange: onDetailModeChange,
    allowInInput: true
  }), [detailMode, detailModeOptions, onDetailModeChange]);
  useShortcutBindings(window, detailModeBindings);
  const filteredRows = useMemo(() => {
    const searchTerms = searchQuery
      .trim()
      .toLocaleLowerCase()
      .split(/\s+/)
      .filter(Boolean);

    if (searchTerms.length === 0) {
      return page.rows;
    }

    return page.rows.filter((row) => (
      searchTerms.every((term) => [
        row.category,
        row.ongoingBudget,
        row.ongoingSpent,
        row.impliedOngoing,
        row.ongoingOverrun,
        row.irregularBudget,
        row.irregularSpent,
        row.irregularRemaining
      ]
        .map((value) => String(value ?? "").toLocaleLowerCase())
        .join(" ")
        .includes(term))
    ));
  }, [page.rows, searchQuery]);
  const maxAbsDelta = useMemo(() => (
    page.rows.reduce((currentMax, row) => {
      const rowMax = Math.max(Math.abs(row.ongoingDelta ?? 0), Math.abs(row.irregularDelta ?? 0));
      return Math.max(currentMax, rowMax);
    }, 0)
  ), [page.rows]);
  const advancedColumns = useMemo(() => createBudgetGridColumns(maxAbsDelta, t), [maxAbsDelta, t]);
  const basicColumns = useMemo(() => createBudgetGridColumnsBasic(t), [t]);
  const activeColumns = getActiveBudgetColumns(detailMode, basicColumns, advancedColumns);
  const activeHeaderGroups = useMemo(
    () => getActiveBudgetHeaderGroups(detailMode, t),
    [detailMode, t]
  );
  const visibleRows = useMemo(
    () => sortRows(filteredRows, activeColumns, sortState),
    [activeColumns, filteredRows, sortState]
  );
  const isEmptyBudgetPage = page.rows.length === 0;
  const budgetFooterProps = getBudgetEmptyFooterProps({
    isVisible: isEmptyBudgetPage,
    hasPreviousYearBudgetRows,
    onImportPreviousYearBudget,
    onLoadSampleData,
    onStartBudgetEntry,
    t
  });

  const handleFilteredRowsChange = useCallback(
    (updateRows) => updateBudgetRowsForVisibleSelection({
      onRowsChange,
      pageRows: page.rows,
      searchQuery,
      sortState,
      updateRows,
      visibleRows
    }),
    [onRowsChange, page.rows, searchQuery, sortState, visibleRows]
  );

  useEffect(() => {
    if (!gridReady || !budgetDraftEntryRequest || budgetDraftEntryRequest === lastHandledDraftRequestRef.current) {
      return;
    }

    if (!gridApiRef.current) {
      return;
    }

    lastHandledDraftRequestRef.current = budgetDraftEntryRequest;
    gridApiRef.current.startDraftRowEdit();
  }, [budgetDraftEntryRequest, gridReady]);

  useShortcutBindings(window, pageBindings);
  return (
    <div class="fill-area" ref={pageRef}>
        <Panel
          className="fill-area"
        toolbar={
          <ControlBar
            boxed={false}
            start={
              <AltHintTarget label="Q" visible={showAltHints}>
                <SearchField
                  className="budget-search-input"
                  inputRef={searchInputRef}
                  onFocusMainArea={focusMainArea}
                  onValueChange={setSearchQuery}
                  placeholder={t("filter")}
                  value={searchQuery}
                />
              </AltHintTarget>
            }
            end={
              <>
                <AltHintTarget label="E" visible={showAltHints} targetRef={detailModeControlRef}>
                  <SegmentedControl
                    options={detailModeOptions}
                    value={detailMode}
                    onChange={onDetailModeChange}
                  />
                </AltHintTarget>
                <AltHintTarget label="N" visible={showAltHints}>
                  <button
                    class="control-bar-button"
                    type="button"
                    onClick={() => gridApiRef.current?.startDraftRowEdit()}
                  >
                    {t("new")}
                  </button>
                </AltHintTarget>
              </>
            }
            />
            }
          footer={budgetFooterProps.footer}
          footerEnd={budgetFooterProps.footerEnd}
        >
        <AltHintTarget label="W" visible={showAltHints} fill>
          <>
            <DataGrid
              columns={activeColumns}
              headerGroups={activeHeaderGroups}
              rows={visibleRows}
              onRowsChange={handleFilteredRowsChange}
              rowEditing={rowEditing}
              sortState={sortState}
              onSortChange={setSortState}
              onApiReady={(api) => {
                gridApiRef.current = api;
                setGridReady(Boolean(api));
              }}
              onFocusContextChange={setFocusContext}
              onSelectionChange={handleSelectionChange}
              shortcuts={{ duplicateRow: false }}
              embedded
            />
            <FloatingActionToolbar
              actions={renderedToolbarActions}
              anchorElement={canOpenContextToolbar ? selectedCellState?.cellElement : null}
              isOpen={toolbarOpen && canOpenContextToolbar}
              offset={4}
              onActionClick={(actionKey) => {
                const actionIndex = toolbarActions.findIndex((action) => action.key === actionKey);
                if (actionIndex < 0) {
                  return;
                }

                setSelectedIndex(actionIndex);
                setToolbarOpen(false);
                toolbarActions[actionIndex].onSelect?.();
              }}
            />
          </>
        </AltHintTarget>
      </Panel>
    </div>
  );
}
